'use client';

import { createClient } from './client';

export interface UserPreferences {
  id: string;
  userId: string;
  subscribedCategories: string[];
  selectedCountries: string[];
  refreshFrequency: number;
  summaryLength: number;
  notificationsEnabled: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

function convertToJsFormat(data: any): UserPreferences {
  return {
    id: data.id,
    userId: data.user_id,
    subscribedCategories: data.subscribed_categories || [],
    selectedCountries: data.selected_countries || [],
    refreshFrequency: data.refresh_frequency || 60,
    summaryLength: data.summary_length || 100,
    notificationsEnabled: data.notifications_enabled ?? true,
    emailNotifications: data.email_notifications ?? false,
    pushNotifications: data.push_notifications ?? false,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

function convertToDbFormat(prefs: Partial<UserPreferences>) {
  const dbFormat: any = {};
  
  if (prefs.userId !== undefined) dbFormat.user_id = prefs.userId;
  if (prefs.subscribedCategories !== undefined) dbFormat.subscribed_categories = prefs.subscribedCategories;
  if (prefs.selectedCountries !== undefined) dbFormat.selected_countries = prefs.selectedCountries;
  if (prefs.refreshFrequency !== undefined) dbFormat.refresh_frequency = prefs.refreshFrequency;
  if (prefs.summaryLength !== undefined) dbFormat.summary_length = prefs.summaryLength;
  if (prefs.notificationsEnabled !== undefined) dbFormat.notifications_enabled = prefs.notificationsEnabled;
  if (prefs.emailNotifications !== undefined) dbFormat.email_notifications = prefs.emailNotifications;
  if (prefs.pushNotifications !== undefined) dbFormat.push_notifications = prefs.pushNotifications;
  
  return dbFormat;
}

export async function getUserPreferences(userId: string): Promise<UserPreferences | null> {
  try {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error.message);
        throw error;
      } else {
        console.log('Get preferences error:', error.message);
        return null;
      }
    }

    return data ? convertToJsFormat(data) : null;
  } catch (error: any) {
    console.log('Schema-related error:', error.message);
    throw error;
  }
}

export async function createUserPreferences(prefs: Partial<UserPreferences>): Promise<UserPreferences | null> {
  try {
    const supabase = createClient();
    const dbData = convertToDbFormat(prefs);

    const { data, error } = await supabase
      .from('user_preferences')
      .insert(dbData)
      .select()
      .single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error.message);
        throw error;
      } else {
        console.log('Create preferences error:', error.message);
        return null;
      }
    }

    return data ? convertToJsFormat(data) : null;
  } catch (error: any) {
    console.log('Schema-related error:', error.message);
    throw error;
  }
}

export async function updateUserPreferences(userId: string, prefs: Partial<UserPreferences>): Promise<UserPreferences | null> {
  try {
    const supabase = createClient();
    const dbData = convertToDbFormat(prefs);
    dbData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('user_preferences')
      .update(dbData)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error.message);
        throw error;
      } else {
        console.log('Update preferences error:', error.message);
        return null;
      }
    }

    return data ? convertToJsFormat(data) : null;
  } catch (error: any) {
    console.log('Schema-related error:', error.message);
    throw error;
  }
}

export async function upsertUserPreferences(userId: string, prefs: Partial<UserPreferences>): Promise<UserPreferences | null> {
  try {
    const supabase = createClient();
    const dbData = convertToDbFormat({ ...prefs, userId });
    dbData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('user_preferences')
      .upsert(dbData, { onConflict: 'user_id' })
      .select()
      .single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error.message);
        throw error;
      } else {
        console.log('Upsert preferences error:', error.message);
        return null;
      }
    }

    return data ? convertToJsFormat(data) : null;
  } catch (error: any) {
    console.log('Schema-related error:', error.message);
    throw error;
  }
}